import { useEffect, useState, useRef, useCallback } from "react";
import { ArrowLeft, Camera, Calendar, Clock, MapPin, DollarSign, Search, ChevronDown } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/AuthContext";
import type { Booking } from "@/types";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { invokeFunctionWithAuth } from "@/lib/supabase/functions";
import { toast } from "sonner";
import { StepProgressBar } from "@/components/tracking/StepProgressBar";
import { useBookingRealtime } from "@/hooks/useBookingRealtime";

export default function FindingFilmmaker() {
  const navigate = useNavigate();
  const { bookingId } = useParams<{ bookingId: string }>();
  const { user } = useAuth();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(3);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [whatsNextOpen, setWhatsNextOpen] = useState(true);
  const loadedRef = useRef<string | null>(null);
  const reloadTriggerRef = useRef(0);

  // Função para recarregar booking (usada pelo hook Realtime e no useEffect inicial)
  const loadBooking = useCallback(async () => {
      if (!bookingId) {
        setLoading(false);
        return;
      }
      
      loadedRef.current = bookingId;
      setLoading(true);
      
      // Fetch from database
      try {
        // Get userId - try from user context, fallback to localStorage
        let userId = user?.id;
        if (!userId) {
          const authSessionStr = localStorage.getItem('auth_session');
          if (authSessionStr) {
            try {
              const authSession = JSON.parse(authSessionStr);
              userId = authSession.userId || authSession.user?.id;
            } catch (e) {
              console.warn('Error parsing auth session:', e);
            }
          }
          
          if (!userId) {
            console.log('⏳ Waiting for user context to initialize...');
            loadedRef.current = null;
            setLoading(false);
            return;
          }
        }

        if (!userId) {
          toast.error('You must be logged in to track bookings');
          navigate('/auth/login');
          setLoading(false);
          return;
        }

        // Fetch booking from database using edge function
        const { data: bookingResponse, error: fetchError } = await invokeFunctionWithAuth('get-booking-details', {
          bookingId: bookingId, // Function handles BK-XXX format
          userId: userId,
          phone: user?.phone
        });

        if (fetchError) {
          console.error('❌ Error fetching booking:', fetchError);
          
          if (fetchError.status === 403) {
            toast.error('You do not have permission to view this booking');
            navigate('/dashboard');
          } else if (fetchError.status === 404) {
            toast.error('Booking not found');
            navigate('/dashboard');
          } else {
            toast.error('Failed to load booking details');
          }
          setLoading(false);
          return;
        }

        if (bookingResponse?.booking) {
          setBooking(bookingResponse.booking as any);
          setLoading(false);
          return;
        }

        // If we get here, booking not found
        console.warn('⚠️ No booking in response');
        toast.error('Booking not found');
        navigate('/dashboard');
        setLoading(false);
      } catch (error: any) {
        console.error('💥 Exception loading booking:', error);
        toast.error('Failed to load booking details');
        setLoading(false);
        if (loadedRef.current === bookingId) {
          loadedRef.current = null;
        }
      }
  }, [bookingId, user, navigate]);

  useEffect(() => {
    // Reset se bookingId mudou
    if (loadedRef.current !== bookingId) {
      loadedRef.current = null;
      setBooking(null);
      setLoading(true);
      reloadTriggerRef.current = 0;
    }
    
    // Prevent multiple executions for same bookingId (apenas no carregamento inicial)
    if (loadedRef.current === bookingId && reloadTriggerRef.current === 0) {
      return;
    }
    
    loadBooking();
  }, [bookingId, user, navigate, loadBooking]);

  // Função chamada quando Realtime detecta mudança
  const handleRealtimeUpdate = useCallback(async (updatedBooking: Booking | null) => {
    if (!bookingId) return;

    // Se receber null, significa que precisa recarregar do servidor
    if (updatedBooking === null) {
      console.log('🔄 Realtime detected change - reloading booking...');
      reloadTriggerRef.current += 1; // Trigger reload
      
      // Recarregar do servidor
      if (user) {
        let userId = user.id;
        if (!userId) {
          const authSessionStr = localStorage.getItem('auth_session');
          if (authSessionStr) {
            try {
              const authSession = JSON.parse(authSessionStr);
              userId = authSession.userId || authSession.user?.id;
            } catch (e) {
              console.warn('Error parsing auth session:', e);
            }
          }
        }

        if (userId) {
          try {
            const { data: bookingResponse, error } = await invokeFunctionWithAuth('get-booking-details', {
              bookingId: bookingId,
              userId: userId,
              phone: user?.phone
            });

            if (!error && bookingResponse?.booking) {
              const newBooking = bookingResponse.booking as Booking;
              setBooking(newBooking);
              
              // Se status mudou para en_route ou assigned, redirecionar para tracking details
              if (newBooking.status === 'en_route' || newBooking.status === 'assigned') {
                console.log('📍 Status changed to', newBooking.status, '- redirecting to tracking details');
                navigate(`/track/${bookingId}`, { replace: true });
              }
            }
          } catch (error) {
            console.error('Error reloading booking:', error);
          }
        }
      }
      return;
    }

    // Mock mode ou booking recebida diretamente
    setBooking(updatedBooking);
    
    // Se status mudou para en_route ou assigned, redirecionar
    if (updatedBooking.status === 'en_route' || updatedBooking.status === 'assigned') {
      console.log('📍 Status changed to', updatedBooking.status, '- redirecting to tracking details');
      navigate(`/track/${bookingId}`, { replace: true });
    }
  }, [bookingId, user, navigate]);

  // Usar hook Realtime para escutar mudanças
  // Passar booking.id quando disponível, caso contrário usar bookingId do route (será convertido na edge function)
  useBookingRealtime({
    bookingId: booking?.id || bookingId, // Preferir booking.id (UUID real), fallback para bookingId do route
    onUpdate: handleRealtimeUpdate,
    enabled: !!booking && !loading, // Só habilitar quando booking estiver carregado
  });

  useEffect(() => {
    if (!booking) return;

    // Calculate actual elapsed time based on booking creation
    const createdAt = new Date(booking.created_at || new Date().toISOString()).getTime();

    // Update estimated time based on actual elapsed time (start at 3-5 mins)
    const initialEstimate = 3 + Math.random() * 2; // Random between 3-5 mins
    const updateEstimatedTime = () => {
      const currentElapsed = Math.floor((Date.now() - createdAt) / 1000);
      const elapsedMinutes = currentElapsed / 60;
      
      // Decrease estimate as time passes, but don't go below 0
      const newEstimate = Math.max(0, initialEstimate - elapsedMinutes);
      setEstimatedTime(newEstimate);
    };

    // Update immediately
    updateEstimatedTime();

    // Update every second
    const interval = setInterval(updateEstimatedTime, 1000);

    // Simulate search progress (up to 90% while waiting)
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          return 90;
        }
        return prev + 0.1; // Slower progress
      });
    }, 200);

    return () => {
      clearInterval(interval);
      clearInterval(progressInterval);
    };
  }, [booking]);

  const handleCancelBooking = async () => {
    if (!booking) return;
    
    // Confirmação
    const confirmed = window.confirm(
      "Are you sure you want to cancel this booking? This action cannot be undone."
    );
    
    if (!confirmed) return;

    try {
      const { data, error } = await invokeFunctionWithAuth('cancel-booking', {
        bookingId: booking.id,
        cancelReason: 'Canceled by customer'
      });

      if (error) {
        console.error('Error canceling booking:', error);
        toast.error(error.message || 'Failed to cancel booking');
        return;
      }

      if (data?.success) {
        toast.success('Booking canceled successfully');
        // Limpar dados do sessionStorage para evitar criar novo booking acidentalmente
        sessionStorage.removeItem('bookingFormData');
        sessionStorage.removeItem('confirmedBooking');
        // Atualizar o booking local
        setBooking({ ...booking, status: 'canceled' });
        // Redirecionar para dashboard
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error canceling booking:', error);
      toast.error('Failed to cancel booking. Please try again.');
    }
  };


  if (loading || !booking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">{loading ? 'Loading booking...' : 'Booking not found'}</p>
      </div>
    );
  }

  const formatDate = (dateStr: string) => 
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  
  const formatTime = (dateStr: string) => 
    new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  
  const formatPrice = (cents: number) => 
    `$${(cents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="min-h-screen bg-background pb-8">
      <header className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3 flex items-center gap-3">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-lg font-semibold">
          Your {formatDate(booking.scheduled_at).split(',')[0]} Booking
        </h1>
      </header>

      <div className="px-4 py-6 space-y-6 max-w-lg mx-auto">
        {/* Finding Filmmaker Card */}
        <Card className="p-5 space-y-4">
          <div className="flex gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Search className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-lg mb-1">
                We are Finding Your Perfect Filmmaker
              </h2>
              <p className="text-sm text-muted-foreground mb-3">
                Step 1 of 4
              </p>
              <StepProgressBar currentStep={1} totalSteps={4} className="h-2" />
              <p className="text-xs text-muted-foreground mt-2">
                Matching you with the best available filmmakers in your area.
              </p>
            </div>
          </div>
        </Card>

        {/* Estimated Wait Time */}
        <Card className="p-6 text-center space-y-2">
          <h3 className="text-4xl font-bold">
            {Math.floor(estimatedTime)}-{Math.ceil(estimatedTime)} mins
          </h3>
          <p className="text-sm text-muted-foreground">Estimated Wait Time</p>
          <Progress value={progress} className="h-2 mt-3" />
          <p className="text-xs text-muted-foreground pt-1">Searching...</p>
        </Card>

        {/* Session Details - Collapsible */}
        <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
          <Card className="overflow-hidden">
            <CollapsibleTrigger className="w-full px-5 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
              <h3 className="font-bold text-lg">Your Session Details</h3>
              <ChevronDown 
                className={`h-5 w-5 transition-transform ${detailsOpen ? 'rotate-180' : ''}`} 
              />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-5 pb-5 space-y-3 border-t">
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Camera className="h-5 w-5 text-primary" />
                    <span>Package</span>
                  </div>
                  <span className="font-medium text-right">{booking.package?.name}</span>
                </div>

                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-5 w-5 text-primary" />
                    <span>Date</span>
                  </div>
                  <span className="font-medium text-right">
                    {formatDate(booking.scheduled_at)}
                  </span>
                </div>

                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-5 w-5 text-primary" />
                    <span>Time</span>
                  </div>
                  <span className="font-medium text-right">
                    {formatTime(booking.scheduled_at)} - {
                      booking.package?.duration_minutes 
                        ? formatTime(new Date(new Date(booking.scheduled_at).getTime() + booking.package.duration_minutes * 60000).toISOString())
                        : ''
                    }
                  </span>
                </div>

                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-5 w-5 text-primary" />
                    <span>Location</span>
                  </div>
                  <span className="font-medium text-right max-w-[60%]">
                    {booking.address_line1}, {booking.city}, {booking.postal_code}
                  </span>
                </div>

                <div className="flex items-center justify-between py-3 border-t pt-4">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <DollarSign className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-foreground">Total Amount</span>
                  </div>
                  <span className="font-bold text-lg">{formatPrice(booking.total_cents)}</span>
                </div>
              </div>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* What's Next - Collapsible */}
        <Collapsible open={whatsNextOpen} onOpenChange={setWhatsNextOpen}>
          <Card className="overflow-hidden">
            <CollapsibleTrigger className="w-full px-5 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
              <h3 className="font-bold text-lg">What's Next?</h3>
              <ChevronDown 
                className={`h-5 w-5 transition-transform ${whatsNextOpen ? 'rotate-180' : ''}`} 
              />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-5 pb-5 space-y-4 border-t pt-4">
                <div className="flex gap-3">
                  <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary-foreground">1</span>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold mb-1">
                      Make Sure You are On-Site to Receive Your Dedicated Filmmaker
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary-foreground">2</span>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold mb-1">
                      Prepare the Shooting Material and People Ahead for Smoother Experience
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary-foreground">3</span>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold mb-1">
                      Share Your Wifi Credentials so the Filmmaker Can Upload the Material as Fast as Possible at the End of Your Shooting
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Don't worry, we do not store or share this information with anyone
                    </p>
                    <Button variant="link" className="p-0 h-auto text-xs mt-1">
                      Share Wifi
                    </Button>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Help Link */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Need Help?{" "}
            <Button variant="link" className="p-0 h-auto text-sm">
              Contact Our Human Support
            </Button>
          </p>
        </div>

        {/* Cancel Booking Button */}
        {booking && booking.status !== 'canceled' && (
          <Button 
            variant="outline" 
            className="w-full h-12 text-base font-semibold rounded-xl border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
            onClick={handleCancelBooking}
          >
            Cancel Booking
          </Button>
        )}
      </div>
    </div>
  );
}
